import React from "react";
import { apiUploadImage } from "./apiClient";
import { getApiBaseUrl } from "../config/appSettings";

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

/**
 * Multi-file image picker. Value is an array of { id?, image, order, _key? }.
 */
export function MultiImageUploadField({ label, value = [], onChange, accept = "image/*", labelClassName }) {
  const labelCls = labelClassName ?? "mb-1 block text-sm font-medium text-slate-700";
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");
  const inputRef = React.useRef(null);

  const items = Array.isArray(value) ? value : [];

  const onFilesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError("");

    const uploaded = [];
    for (const file of files) {
      const res = await apiUploadImage("/api/Uploads/image", file);
      if (!res.ok) {
        setError(res.error ?? `Failed to upload ${file.name}`);
        continue;
      }
      const uploadedPath = String(res.data?.path ?? res.data?.Path ?? "").trim();
      if (!uploadedPath) {
        setError("Upload succeeded but no image path was returned.");
        continue;
      }
      uploaded.push({
        _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        image: uploadedPath,
        order: items.length + uploaded.length
      });
    }

    if (uploaded.length) {
      onChange([...items, ...uploaded].map((item, index) => ({ ...item, order: index })));
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (index) => {
    const next = items.filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i }));
    onChange(next);
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(next.map((row, i) => ({ ...row, order: i })));
  };

  return (
    <div className="block min-w-0">
      <span className={labelCls}>{label}</span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        onChange={onFilesChange}
        className="block w-full text-sm text-slate-600"
      />
      {uploading && <span className="mt-1 block text-xs text-slate-500">Uploading…</span>}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
      {items.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {items.map((item, index) => {
            const src = resolveMediaUrl(item.image);
            const key = item.id ?? item._key ?? `${item.image}-${index}`;
            return (
              <div key={key} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {src ? (
                  <img src={src} alt="" className="h-28 w-full object-cover" />
                ) : (
                  <div className="flex h-28 items-center justify-center text-xs text-slate-400">No image</div>
                )}
                <div className="flex items-center justify-between gap-1 px-2 py-1.5">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      className="rounded px-1.5 py-0.5 text-xs text-slate-600 hover:bg-white disabled:opacity-40"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === items.length - 1}
                      className="rounded px-1.5 py-0.5 text-xs text-slate-600 hover:bg-white disabled:opacity-40"
                    >
                      ↓
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAt(index)}
                    className="text-xs text-slate-500 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-1 text-xs text-slate-500">Select multiple images at once. Drag order with ↑ ↓.</p>
    </div>
  );
}
